import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Types (extracted from Settings.tsx)
interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  // Business info removed - now managed at agent level
  notifications: {
    email: boolean;
    sms: boolean;
    frequency: string;
  };
}

interface SubscriptionDetails {
  id: string;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  amount: number;
  currency: string;
  interval: string;
  paymentMethod: {
    type: string;
    card: {
      brand: string;
      last4: string;
      expMonth: number;
      expYear: number;
    } | null;
  } | null;
  nextInvoice: {
    amount: number;
    date: string;
  } | null;
}

interface BillingHistoryItem {
  id: string;
  amount: number;
  currency: string;
  status: string;
  date: string;
  invoiceUrl: string;
  pdfUrl: string;
  description: string;
}

// API helper function
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// Query Keys
export const settingsKeys = {
  all: ['settings'] as const,
  userSettings: () => [...settingsKeys.all, 'user'] as const,
  subscription: () => [...settingsKeys.all, 'subscription'] as const,
  billingHistory: () => [...settingsKeys.all, 'billing-history'] as const,
};

// Custom Hooks

// Fetch user settings
export const useUserSettings = () => {
  return useQuery({
    queryKey: settingsKeys.userSettings(),
    queryFn: async (): Promise<{ settings: Partial<UserProfile> }> => {
      return apiRequest('/api/user/settings');
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Fetch subscription details
export const useSubscriptionDetails = (enabled: boolean = true) => {
  return useQuery({
    queryKey: settingsKeys.subscription(),
    queryFn: async (): Promise<{ hasSubscription: boolean; subscription?: SubscriptionDetails }> => {
      return apiRequest('/api/subscription-details');
    },
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes for billing data
  });
};

// Fetch billing history
export const useBillingHistory = (enabled: boolean = true) => {
  return useQuery({
    queryKey: settingsKeys.billingHistory(),
    queryFn: async (): Promise<{ billingHistory: BillingHistoryItem[] }> => {
      return apiRequest('/api/billing-history');
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Mutations

// Update user settings
export const useUpdateSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (profile: UserProfile): Promise<void> => {
      await apiRequest('/api/user/settings', {
        method: 'PUT',
        body: JSON.stringify(profile),
      });
    },
    onSuccess: () => {
      // Invalidate and refetch user settings
      queryClient.invalidateQueries({ queryKey: settingsKeys.userSettings() });
    },
  });
};

// Change password
export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }): Promise<void> => {
      await apiRequest('/api/user/password', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  });
};

// Cancel subscription
export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (cancelAtPeriodEnd: boolean = true): Promise<void> => {
      await apiRequest('/api/cancel-subscription', {
        method: 'POST',
        body: JSON.stringify({ cancelAtPeriodEnd }),
      });
    },
    onSuccess: () => {
      // Invalidate subscription and billing data
      queryClient.invalidateQueries({ queryKey: settingsKeys.subscription() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.billingHistory() });
      // We'll need to refresh auth context data too, but that will be handled in the component
    },
  });
};

// Resume subscription
export const useResumeSubscription = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiRequest('/api/resume-subscription', {
        method: 'POST',
      });
    },
    onSuccess: () => {
      // Invalidate subscription and billing data
      queryClient.invalidateQueries({ queryKey: settingsKeys.subscription() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.billingHistory() });
    },
  });
};

// Delete account
export const useDeleteAccount = () => {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiRequest('/api/user/account', {
        method: 'DELETE',
      });
    },
  });
};

// Create customer portal session
export const useCreatePortalSession = () => {
  return useMutation({
    mutationFn: async (): Promise<{ url: string }> => {
      return apiRequest('/api/create-portal-session', {
        method: 'POST',
      });
    },
  });
};